import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../../services/api';
import { PageHeader, StatCard, Spinner, BandBadge } from '../../components/shared/index';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { UsersIcon, FlagIcon, ChartBarIcon, BoltIcon } from '@heroicons/react/24/outline';

const BAND_COLORS = { 'X': '#16a34a', 'E+': '#2563eb', 'E': '#6b7280', 'E-': '#dc2626' };

export default function Analytics() {
  const { data: overview } = useQuery({ queryKey: ['analytics-overview'], queryFn: () => analyticsApi.overview().then(r => r.data) });
  const { data: distHist  } = useQuery({ queryKey: ['analytics-distribution'], queryFn: () => analyticsApi.distribution().then(r => r.data) });
  const { data: atRisk    } = useQuery({ queryKey: ['analytics-attrition'], queryFn: () => analyticsApi.attritionRisk().then(r => r.data) });
  const { data: manEff    } = useQuery({ queryKey: ['analytics-managers'], queryFn: () => analyticsApi.managerEffectiveness().then(r => r.data) });
  const { data: teamHlth  } = useQuery({ queryKey: ['analytics-team-health'], queryFn: () => analyticsApi.teamHealth().then(r => r.data) });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader title="Analytics" subtitle="Org-wide performance intelligence" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total employees"  value={overview?.headcount ?? '—'}    color="blue"   icon={UsersIcon} />
        <StatCard title="Active PIPs"      value={overview?.active_pips ?? '—'}  color="red"    icon={FlagIcon} />
        <StatCard title="Latest cycle"     value={overview?.latest_cycle?.status ?? '—'} color="purple" icon={ChartBarIcon} subtitle={overview?.latest_cycle?.name} />
        <StatCard title="Review completion" value={overview?.review_completion ? `${Math.round((overview.review_completion.submitted/overview.review_completion.total)*100)||0}%` : '—'} color="green" icon={BoltIcon} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Distribution trend */}
        {distHist?.length > 0 && (
          <div className="card p-5">
            <h2 className="section-title">Band distribution — last {distHist.length} cycles</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={distHist.map(c => ({ cycle: c.cycle.slice(0,8), X: c.distribution?.X?.pct||0, 'E+': c.distribution?.['E+']?.pct||0, E: c.distribution?.E?.pct||0, 'E-': c.distribution?.['E-']?.pct||0 }))}>
                <XAxis dataKey="cycle" tick={{ fontSize:10 }} />
                <YAxis tickFormatter={v=>`${v}%`} tick={{ fontSize:10 }} />
                <Tooltip formatter={v=>`${v}%`} />
                <Legend />
                {['X','E+','E','E-'].map(b => <Bar key={b} dataKey={b} stackId="a" fill={BAND_COLORS[b]} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Team health */}
        {teamHlth?.length > 0 && (
          <div className="card p-5">
            <h2 className="section-title">Team health by department</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {teamHlth.map(t => (
                <div key={t.department_id} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-700">{t.department_name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{t.headcount} employees</span>
                    {t.active_pips > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">{t.active_pips} PIPs</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Attrition risk */}
      {atRisk?.length > 0 && (
        <div className="card p-5 mb-6">
          <h2 className="section-title text-red-700">Attrition risk — {atRisk.length} employees flagged</h2>
          <div className="table-wrap mt-3">
            <table className="table">
              <thead><tr>{['Employee','Department','Manager','Risk level','Factors'].map(h=><th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {atRisk.map(r => (
                  <tr key={r.employee_id} className="tr-hover">
                    <td className="td font-medium">{r.employee_name}</td>
                    <td className="td text-gray-500">{r.department || '—'}</td>
                    <td className="td text-gray-500">{r.manager || '—'}</td>
                    <td className="td"><span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">{r.risk_level}</span></td>
                    <td className="td text-xs text-gray-500">{r.factors?.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manager effectiveness */}
      {manEff?.length > 0 && (
        <div className="card p-5">
          <h2 className="section-title">Manager effectiveness</h2>
          <div className="table-wrap mt-3">
            <table className="table">
              <thead><tr>{['Manager','Direct reports','Active PIPs on team'].map(h=><th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {manEff.map(m => (
                  <tr key={m.id} className="tr-hover">
                    <td className="td font-medium">{m.name}</td>
                    <td className="td">{m.direct_reports}</td>
                    <td className="td">{m.active_pips > 0 ? <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">{m.active_pips}</span> : <span className="text-green-600 text-xs">0</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
